/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Component, Inject, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {RemovalPointType, RPTypeFromRP} from '../../../_forms/removal-point-type';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ToastrService} from 'ngx-toastr';
import {RemovalPointService} from '../../../_services/removal-point.service';
import {Helpers} from '../../../helpers';
import {RemovalPoint} from '../../../_models/removal-point';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {PhoneNumber} from '../../../_models/phone-number';
import {EDIT, RP_NAME_MAX_LENGTH} from '../../../_enums/constants';
import {CREATE_SUCCESS, I18nService, UPDATE_SUCCESS} from '../../../_services/i18n.service';
import {ToolsHelper} from '../../../_helpers/tools-helper';

export interface DialogRemovalPointFormData {
  mode: string;
  removalPoint: RemovalPoint;
  thirdPartyId: number;
}

@Component({
  selector: 'app-dialog-removal-point-form',
  templateUrl: './dialog-removal-point-form.component.html',
  styleUrls: ['./dialog-removal-point-form.component.scss']
})
export class DialogRemovalPointFormComponent implements OnInit {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROPERTIES                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public formData: RemovalPointType;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS GROUPS                                                                                          ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public removalPointFG: FormGroup;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGICS METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    public dialogRef: MatDialogRef<DialogRemovalPointFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogRemovalPointFormData,
    private formBuilder: FormBuilder,
    private i18n: I18nService,
    private removalPointService: RemovalPointService,
    private toastr: ToastrService
  ) {
    this.formData = RPTypeFromRP(this.data.removalPoint);
  }

  ngOnInit() {
    this.initForm();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                                     FORM CONTROLS                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get addressFC(): AbstractControl {
    return this.removalPointFG.get('address');
  }

  get nameFC(): AbstractControl {
    return this.removalPointFG.get('name');
  }

  get townshipFC(): AbstractControl {
    return this.removalPointFG.get('township');
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS INITIALISATION                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private initForm() {
    this.removalPointFG = this.formBuilder.group({
      name: [
        this.formData.name,
        [
          Validators.maxLength(RP_NAME_MAX_LENGTH),
          Validators.pattern(/^[a-zA-Z0-9]+((['\s-.,][a-zA-Z0-9])?[a-zA-Z0-9]*)*$/i),
          Validators.required
        ]
      ],
      address: [
        this.formData.address,
        [
          Validators.maxLength(RP_NAME_MAX_LENGTH),
          Validators.pattern(/^[a-zA-Z0-9]+((['\s-.,][a-zA-Z0-9])?[a-zA-Z0-9]*)*$/i),
          Validators.required
        ]
      ],
      address2: [{value: this.formData.address2, disabled: true}],
      township: [this.formData.township, [Validators.required]],
      townshipName: [this.formData.townshipName],
      zip: [this.formData.zip]
    });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALUES GETTERS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get isModeEdit(): boolean {
    return this.data && this.data.mode === EDIT;
  }

  get hasPhoneNumbers(): boolean {
    return (this.formData.newPhones.length + this.formData.mobilePhones.length) > 0 || this.formData.newPhones.length > 0;
  }

  get rp(): RemovalPoint | null {
    return this.data ? this.data.removalPoint : null;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                                    LISTENERS                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public cancel(e): void {
    e.preventDefault();
    this.dialogRef.close(null);
  }

  public updatePhones(e?: RemovalPoint): void {
    if (e) {
      if (this.data && this.data.removalPoint) {
        this.data.removalPoint.mobilePhones = e.mobilePhones;
        this.data.removalPoint.phones = e.phones;
        this.data.removalPoint = ToolsHelper.hardCopy(this.data.removalPoint);
      }

      this.formData.newPhones = e.mobilePhones.map((it: PhoneNumber) => it.number);
      this.formData.newPhones = this.formData.newPhones.concat(e.phones.map((it: PhoneNumber) => it.number));
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS SUBMITS                                                                                         ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public removalPointFormSubmit() {
    if (this.removalPointFG.valid && this.hasPhoneNumbers) {
      Helpers.setLoading(true);

      if (this.isModeEdit) {
        this.removalPointService.update(this.data.removalPoint.id, this.removalPointFG.value, {version: 0.3}).subscribe(
          (res: HttpResponse<RemovalPoint>) => {
            this.handleSuccess(UPDATE_SUCCESS);
            this.dialogRef.close(res.body);
          }, (err: HttpErrorResponse) => this.handleError(err));

      } else {
        this.removalPointService.create(this.data.thirdPartyId, this.removalPointFG.value, {version: 0.3}).subscribe(
          (res: HttpResponse<RemovalPoint>) => {
            this.removalPointService.addPhoneNumbers(res.body.id, this.formData.newPhones, {version: 0.3}).subscribe((res2: HttpResponse<RemovalPoint>) => {
              this.dialogRef.close(res2.body);
              this.handleSuccess(CREATE_SUCCESS);
            }, (err: HttpErrorResponse) => this.handleError(err));
          }, (err: HttpErrorResponse) => this.handleError(err));
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PRIVATE METHODS                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private handleSuccess(msgKey: string | number): void {
    Helpers.setLoading(false);
    this.toastr.success(this.i18n.get(msgKey));
  }

  private handleError(err: HttpErrorResponse): void {
    Helpers.setLoading(false);
    this.toastr.error(`${err.status} - ${this.i18n.get(err.status)}`);
  }

}
